import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { UPDATE_LAB_NOTE } from '@retainerclub/partner-api';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { BaseLoader, Button, ErrorMessage } from '../../../../shared';
import convertPatientDateFields from '../../../../utils/convertPatientDateFields';
import { initialValues, schema } from './helpers';
import ReferralsProductionListModalFormSubmit from './ReferralsProductionListModalFormSubmit';

function ReferralsProductionListModalFormInner({
  record,
  open,
  handleCloseModal,
  onUpdateSuccess,
  children,
}) {
  const navigate = useNavigate();
  const [mutationErrors, setMutationErrors] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const [update, { error, loading }] = useMutation(UPDATE_LAB_NOTE);

  // Reset success and error state when record or modal open state changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on record id and open so new patient or reopen clears state
  useEffect(() => {
    setUpdateSuccess(false);
    setMutationErrors(null);
  }, [record?.id, open]);

  const memoizedInitialValues = useMemo(
    () => initialValues({ record }),
    [record],
  );

  const {
    register,
    handleSubmit,
    formState: { isLoading, isSubmitting, isValid },
    formState,
    getValues,
    setValue,
    control,
    ...rest
  } = useForm({
    resolver: yupResolver(schema()),
    defaultValues: memoizedInitialValues,
    values: memoizedInitialValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (record && !record?.settingsOverride) {
      setValue(
        'settingGroupOptionIds',
        record.partnerLocation.settingGroupOptionIds,
      );
    }
  }, [record, setValue]);

  async function submitRecord(values) {
    try {
      // Convert Patient-specific date fields for GraphQL
      const convertedValues = convertPatientDateFields(values, getValues);

      const params = {
        variables: {
          input: {
            input: convertedValues,
            clientMutationId: crypto.randomUUID(),
          },
        },
      };

      const { data } = await update(params);

      const mErrors = data.updateLabNote.errorMessages;

      if (mErrors.length > 0) {
        setMutationErrors(mErrors);
      } else {
        setMutationErrors(null);
        setUpdateSuccess(true);
        onUpdateSuccess?.();
      }
    } catch (err) {
      console.error(err);
      setMutationErrors([
        {
          message: 'Unable to update case details. Please try again.',
          path: 'base',
        },
      ]);
    }
  }

  if (loading) {
    return <BaseLoader title="Lab Notes" />;
  }

  if (updateSuccess && record) {
    return (
      <div className="flex flex-col items-center pt-2">
        <Button
          type="button"
          name="Make an In Office Purchase"
          onClick={() => {
            navigate(`/customers/patients/${record.id}/paid-in-office-shop`);
            handleCloseModal();
          }}
          className="inline-flex justify-center px-6 py-3 font-semibold text-base"
        />
      </div>
    );
  }

  return (
    <FormProvider
      {...rest}
      control={control}
      register={register}
      handleSubmit={handleSubmit}
      formState={formState}
      getValues={getValues}
      setValue={setValue}
    >
      <form onSubmit={handleSubmit(submitRecord)} noValidate>
        <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-6">
          <div className="col-span-6 grid grid-cols-1 gap-4 sm:grid-cols-6 md:col-span-6">
            {children}

            <div className="col-span-full">
              {error && (
                <div className="col-span-full mt-4">
                  <ErrorMessage error={error} />
                </div>
              )}

              {mutationErrors && (
                <div className="col-span-full mt-4">
                  <ErrorMessage errorMessages={mutationErrors} />
                </div>
              )}
              <ReferralsProductionListModalFormSubmit
                loading={loading}
                isLoading={isLoading}
                isSubmitting={isSubmitting}
                isValid={isValid}
                handleSubmit={handleSubmit(submitRecord)}
              />
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

ReferralsProductionListModalFormInner.propTypes = {
  record: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  onUpdateSuccess: PropTypes.func,
  children: PropTypes.node.isRequired,
};

export default ReferralsProductionListModalFormInner;
